/**
 * TanStack Router setup — code-based routing.
 *
 * Layout:
 *   /login, /signup           → bare auth pages (no shell)
 *   everything else           → AppShell (top nav, banner, modals)
 */

import { useEffect } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useNavigate,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { authApi } from "./api/queries/auth";
import { LoginPage } from "./pages/Login";
import { SignupPage } from "./pages/Signup";
import { DashboardPage } from "./pages/Dashboard";
import { DeviceDetailPage } from "./pages/DeviceDetail";
import { WorkOrdersPage } from "./pages/WorkOrders";
import { WorkOrderDetailPage } from "./pages/WorkOrderDetail";
import { SettingsPage } from "./pages/Settings";
import { ActivityPage } from "./pages/ActivityPage";
import { AppShell } from "./App";

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_protected",
  component: ProtectedShell,
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/",
  component: DashboardPage,
});

const deviceDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/devices/$id",
  component: DeviceDetailPage,
});

const workOrdersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/work-orders",
  component: WorkOrdersPage,
});

const workOrderDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/work-orders/$id",
  component: WorkOrderDetailPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/settings",
  component: SettingsPage,
});

const activityRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/activity",
  component: ActivityPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  signupRoute,
  protectedRoute.addChildren([
    dashboardRoute,
    deviceDetailRoute,
    workOrdersRoute,
    workOrderDetailRoute,
    settingsRoute,
    activityRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function ProtectedShell() {
  const nav = useNavigate();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => authApi.me(),
    retry: false,
  });

  // TanStack Router's `redirect()` is only valid from `beforeLoad` / `loader`
  // — throwing it from a component body crashes into React's error boundary
  // and shows the router's default "Error" screen. Use the imperative
  // navigator instead.
  useEffect(() => {
    if (me.isError) {
      nav({ to: "/login" });
    }
  }, [me.isError, nav]);

  if (me.isLoading || me.isError) {
    return (
      <main className="min-h-screen grid place-items-center">
        <span className="spinner" aria-hidden="true" />
      </main>
    );
  }
  return <AppShell />;
}
